import type { ReactNode } from "react";
import { Icon, type ThreadEntry } from "@omnabase/ui";
import { FORM_CARD_MAPPINGS, type FormPlacedBy, type Ticket, type TicketSubmission } from "@omnabase/shared";

/** The form builder, round three (brief Outcome 4; red team findings 19 and 20): a form's submission at the top of the ticket's first
 *  message, in place of its "Label: value" lines: the form's filled fields in its order (a hidden field included, a licence already masked by
 *  the server) and the project. Files stay the message's attachments. The form editor round (the brief's item 6, questions 11 and 16): less
 *  noise. How the project was chosen is the tooltip of its name (reachable by keyboard), no longer a line; a value the company's own site
 *  vouched for carries a small shield with its explanation as a tooltip; typed values and values the page filled in carry no mark at all. */
const CHOSEN: Record<FormPlacedBy, string> = { field: "picked in the form", placement: "set by the page the form sits on", default: "the form's default" };
const VERIFIED_HINT = "Verified: the company's own site sent this value with its key.";

export function SubmissionBlock({ submission: s, projectName }: { submission: TicketSubmission; projectName: string | null }) {
  const filled = s.fields.filter((f) => f.value);
  return (
    <div className="ob-submission" aria-label="Form submission">
      <div className="ob-submission__head">
        <span>Sent with the form <span className="ob-submission__form">{s.formName}</span></span>
        {projectName && (s.project.chosenBy
          ? <span className="ob-submission__project" data-tip={`How it was chosen: ${CHOSEN[s.project.chosenBy]}`} tabIndex={0}>{projectName}</span>
          : <span className="ob-submission__project">{projectName}</span>)}
      </div>
      <dl className="ob-submission__list">
        {filled.map((f) => (
          <div key={f.key} className="ob-submission__row">
            <dt>{f.label}<span className="ob-sr-only">: </span></dt>
            <dd>
              <span className="ob-submission__value">{f.shown}</span>
              <ValueMark field={f} />
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** The site-fields round (2026-10-29; brief rule 4, the red team's finding 9): the site fields a message carried (a forum post's private
 *  data, or what a team reply carried back), in the submission's look, under the message's words: the head names the site ("From <host>" for
 *  what came in, "Sent to <host>" on a team reply), each value's line breaks kept, a web address (http or https only) a link in a new tab, no
 *  marks (the site relays what was typed; nobody vouched for it). */
export function SiteFieldsBlock({ fields, host, sent = false }: { fields: { key: string; label: string; value: string }[]; host: string | null; /** a team reply's fields, on their way to the site */ sent?: boolean }) {
  const site = host ?? "the site";
  return (
    <div className="ob-submission ob-submission--site" aria-label="Site fields">
      <div className="ob-submission__head"><span>{sent ? `Sent to ${site}` : `From ${site}`}</span></div>
      <dl className="ob-submission__list">
        {fields.map((f) => (
          <div key={f.key} className="ob-submission__row">
            <dt>{f.label}<span className="ob-sr-only">: </span></dt>
            <dd><span className="ob-submission__value">{linked(f.value)}</span></dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
const WEB_ADDRESS = /https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]]/g;
/** a value as text with its web addresses as links; anything else (a `javascript:` text) stays text */
function linked(value: string): ReactNode[] {
  const out: ReactNode[] = []; let last = 0;
  for (const m of value.matchAll(WEB_ADDRESS)) {
    out.push(value.slice(last, m.index));
    out.push(<a key={m.index} href={m[0]} target="_blank" rel="noopener noreferrer">{m[0]}</a>);
    last = m.index + m[0].length;
  }
  out.push(value.slice(last));
  return out;
}

/** the form builder, round three: a form ticket's first block shows the submission as tidy fields in place of its "Label: value" lines */
export function withSubmission(entries: ThreadEntry[], ticket: Ticket, projects: readonly { id: string; name: string }[]): ThreadEntry[] {
  const s = ticket.submission;
  if (!s || !entries[0]) return entries;
  const projectName = projects.find((p) => p.id === s.project.id)?.name ?? null;
  return [{ ...entries[0], submission: <SubmissionBlock submission={s} projectName={projectName} /> }, ...entries.slice(1)];
}

/** a value's mark (the form editor round): a small shield when the company's site sent it with its key, its explanation as a tooltip that the
 *  keyboard reaches too; nothing for a typed value or one the page filled in (question 11) */
function ValueMark({ field: f }: { field: TicketSubmission["fields"][number] }) {
  if (!f.verified) return null;
  return <span className="ob-submission__mark ob-submission__mark--verified" aria-label="Verified" data-tip={VERIFIED_HINT} tabIndex={0} role="img"><Icon name="shield" size="sm" /></span>;
}

/** Round three's "From the form" block on the customer card: the mapped values the person sent (name, company, licence masked, order number),
 *  a vouched value with the shield, the rest without a mark (the form editor round). Its own rows (`ob-info__fact`), so the card's Information rows stay the
 *  record's: a typed company never reads as the customer's company. */
export function FormFacts({ submission }: { submission: TicketSubmission | null | undefined }) {
  const order = (m: string | null) => (FORM_CARD_MAPPINGS as readonly string[]).indexOf(m ?? "");
  const mapped = (submission?.fields ?? []).filter((f) => f.value && order(f.mapping) >= 0).sort((a, b) => order(a.mapping) - order(b.mapping));
  if (!mapped.length) return null;
  return (
    <div className="ob-info__block">
      <h3 className="ob-info__title">From the form</h3>
      <dl className="ob-info__list">
        {mapped.map((f) => (
          <div className="ob-info__fact" key={f.key}>
            <dt>{f.label}</dt>
            <dd>{f.shown}<ValueMark field={f} /></dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
